/**
 * Photos Gallery Screen
 * 
 * Main photo gallery with filters, grid view, and upload functionality.
 */

import React, { useState, useCallback, useMemo } from "react";
import { View, Alert } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  usePhotos,
  usePhotoUpload,
  usePhotoDownload,
  PhotoGrid,
  PhotoFiltersBar,
  PhotoUploadButton,
  PhotoViewer,
} from "@/modules/photos";
import type { PhotoWithRelations, PhotoFilters } from "@/modules/photos";

/**
 * Photos gallery screen
 */
export default function PhotosScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { user, activeProfile, userTeam, activeWeekend } = useAuth();
  const userId = activeProfile?.id || user?.id;

  // State
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoWithRelations | null>(null);
  const [showViewer, setShowViewer] = useState(false);

  // Build filters based on active filter - memoized to prevent infinite loops
  const filters = useMemo((): PhotoFilters => {
    const result: PhotoFilters = {};

    // Always filter by active weekend if available
    if (activeWeekend?.id) {
      result.weekendId = activeWeekend.id;
    }

    switch (activeFilter) {
      case "featured":
        result.isFeatured = true;
        break;
      case "my":
        if (userId) result.userId = userId;
        break;
      case "team":
        if (userTeam?.id) result.teamId = userTeam.id;
        break;
      case "general":
        result.activityType = "general";
        break;
      case "challenge":
        result.activityType = "challenge";
        break;
      case "speurtocht":
        result.activityType = "speurtocht";
        break;
      case "agenda_event":
        result.activityType = "agenda_event";
        break;
    }

    return result;
  }, [activeFilter, userId, userTeam?.id, activeWeekend?.id]);

  // Photos hook
  const {
    photos,
    isLoading,
    isRefreshing,
    hasMore,
    totalCount,
    refresh,
    loadMore,
    deletePhoto,
    toggleVisibility,
    toggleFeatured,
  } = usePhotos({
    filters,
    realtime: true,
  });

  // Upload hook
  const {
    isUploading,
    progress,
    pickPhotos,
    takePhoto,
    uploadMultiple,
    uploadPhoto,
  } = usePhotoUpload();

  // Download hook
  const { downloadMultiple } = usePhotoDownload();

  /**
   * Handle filter change
   */
  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
  };

  /**
   * Handle photo press
   */
  const handlePhotoPress = (photo: PhotoWithRelations) => {
    setSelectedPhoto(photo);
    setShowViewer(true);
  };

  /**
   * Handle close viewer
   */
  const handleCloseViewer = () => {
    setShowViewer(false);
    setSelectedPhoto(null);
  };

  /**
   * Handle pick photos from gallery
   */
  const handlePickPhotos = async () => {
    const selectedPhotos = await pickPhotos();
    if (selectedPhotos.length > 0) {
      Alert.alert(
        "Foto's uploaden",
        `${selectedPhotos.length} foto('s) geselecteerd. Wil je deze uploaden?`,
        [
          { text: "Annuleren", style: "cancel" },
          {
            text: "Uploaden",
            onPress: async () => {
              await uploadMultiple(selectedPhotos);
              refresh();
            },
          },
        ]
      );
    }
  };

  /**
   * Handle take photo with camera
   */
  const handleTakePhoto = async () => {
    const photo = await takePhoto();
    if (photo) {
      await uploadPhoto(photo);
      refresh();
    }
  };

  /**
   * Handle delete photo
   */
  const handleDeletePhoto = async (photoId: string) => {
    const success = await deletePhoto(photoId);
    if (success) {
      handleCloseViewer();
    } else {
      Alert.alert("Fout", "Kon foto niet verwijderen");
    }
  };

  /**
   * Handle download selected photos
   */
  const handleDownloadSelected = async (photoIds: string[]) => {
    const photosToDownload = photos.filter(p => photoIds.includes(p.id));
    await downloadMultiple(photosToDownload);
  };

  /**
   * Handle toggle visibility
   */
  const handleToggleVisibility = async (photoId: string, visible: boolean) => {
    const success = await toggleVisibility(photoId, visible);
    if (success && selectedPhoto?.id === photoId) {
      setSelectedPhoto(prev => prev ? { ...prev, is_visible: visible } : null);
    }
  };

  /**
   * Handle toggle featured
   */
  const handleToggleFeatured = async (photoId: string, featured: boolean) => {
    const success = await toggleFeatured(photoId, featured);
    if (success && selectedPhoto?.id === photoId) {
      setSelectedPhoto(prev => prev ? { ...prev, is_featured: featured } : null);
    }
  };

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["left", "right"]}
    >
      {/* Filters */}
      <PhotoFiltersBar
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        weekendName={activeWeekend?.name}
        showActivityTypes
      />

      {/* Photo grid */}
      <PhotoGrid
        photos={photos}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        hasMore={hasMore}
        onRefresh={refresh}
        onLoadMore={loadMore}
        onPhotoPress={handlePhotoPress}
        onDownloadSelected={handleDownloadSelected}
        emptyMessage="Nog geen foto's. Upload de eerste!"
      />

      {/* Upload FAB */}
      <PhotoUploadButton
        onPickPhotos={handlePickPhotos}
        onTakePhoto={handleTakePhoto}
      />

      {/* Photo viewer modal */}
      <PhotoViewer
        visible={showViewer}
        photo={selectedPhoto}
        onClose={handleCloseViewer}
        onDelete={handleDeletePhoto}
        onToggleVisibility={handleToggleVisibility}
        onToggleFeatured={handleToggleFeatured}
      />
    </SafeAreaView>
  );
}

